import { useState, useCallback, useEffect } from 'react';
import { FileText, Clock, GitBranch, Wand2 } from 'lucide-react';
import { MarkdownEditor } from './MarkdownEditor';
import { SpecDocumentList } from './SpecDocumentList';
import { VersionHistory, useMockVersionHistory, type Version } from './VersionHistory';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDateTime } from '@/lib/utils';
import { specDocumentService, type SpecDocument as ServiceSpecDocument, type SpecDocumentStatus } from '@/services/specDocumentService';
import type { SpecDocument } from '@/types';

interface SpecDocumentEditorProps {
  projectId: string;
  document?: SpecDocument | null;
  onSave?: (content: string) => Promise<unknown>;
  isLoading?: boolean;
}

const STATUS_COLORS: Record<SpecDocumentStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  review: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  deprecated: 'bg-red-100 text-red-700',
};

const STATUS_LABELS: Record<SpecDocumentStatus, string> = {
  draft: '초안',
  review: '검토 중',
  approved: '승인됨',
  deprecated: '폐기됨',
};

export function SpecDocumentEditor({
  projectId,
  document: initialDocument,
  onSave: externalOnSave,
  isLoading: externalIsLoading,
}: SpecDocumentEditorProps) {
  const [selectedDocument, setSelectedDocument] = useState<ServiceSpecDocument | null>(null);
  const [content, setContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);
  const versions = useMockVersionHistory();

  // Load full document when selected
  const loadDocument = useCallback(async (doc: ServiceSpecDocument) => {
    try {
      setIsLoadingDocument(true);
      const fullDoc = await specDocumentService.getDocument(projectId, doc.id);
      setSelectedDocument(fullDoc);
      setContent(fullDoc.content || '');
    } catch (error) {
      console.error('Failed to load document:', error);
    } finally {
      setIsLoadingDocument(false);
    }
  }, [projectId]);

  // Handle document selection from list
  const handleSelectDocument = useCallback((doc: ServiceSpecDocument) => {
    loadDocument(doc);
  }, [loadDocument]);

  // Handle save
  const handleSave = async () => {
    if (!selectedDocument) return;

    setIsSaving(true);
    try {
      if (externalOnSave) {
        await externalOnSave(content);
      } else {
        await specDocumentService.updateDocument(projectId, selectedDocument.id, {
          content,
        });
      }
    } catch (error) {
      console.error('Failed to save document:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const generateWithAI = () => {
    // Placeholder for AI generation
    console.log('Generate with AI');
  };

  const handleRestoreVersion = (version: Version) => {
    setContent(version.content);
  };

  // Initialize with initialDocument if provided
  useEffect(() => {
    if (initialDocument && !selectedDocument) {
      setContent(initialDocument.content || '');
    }
  }, [initialDocument, selectedDocument]);

  const isLoading = externalIsLoading || isLoadingDocument;
  const currentDocument = selectedDocument;

  return (
    <div className="flex h-full">
      {/* Document List Sidebar */}
      <SpecDocumentList
        projectId={projectId}
        selectedDocumentId={currentDocument?.id}
        onSelectDocument={handleSelectDocument}
        className="w-64 shrink-0"
      />

      {/* Editor Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {isLoading ? (
          <div className="flex-1 p-4">
            <div className="space-y-4">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-4 w-96" />
              <Skeleton className="h-[400px] w-full" />
            </div>
          </div>
        ) : !currentDocument ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="text-lg font-medium">문서를 선택하세요</p>
              <p className="text-sm mt-1">
                왼쪽 목록에서 문서를 선택하거나 새 문서를 만드세요
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between border-b px-4 py-3 shrink-0">
              <div className="flex items-center gap-3">
                <span className="text-xl">
                  {specDocumentService.getCategoryIcon(currentDocument.category || 'other')}
                </span>
                <div>
                  <h3 className="font-medium">{currentDocument.title}</h3>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDateTime(currentDocument.updated_at)}
                    </span>
                    <span className="flex items-center gap-1">
                      <GitBranch className="h-3 w-3" />
                      v{currentDocument.version}
                    </span>
                    {(currentDocument.word_count ?? 0) > 0 && (
                      <span>{currentDocument.word_count}자</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <VersionHistory
                  versions={versions}
                  currentVersion={currentDocument.version}
                  onRestore={handleRestoreVersion}
                />
                <Button variant="outline" size="sm" onClick={generateWithAI}>
                  <Wand2 className="mr-1 h-4 w-4" />
                  AI 생성
                </Button>
                <Badge className={STATUS_COLORS[currentDocument.status]}>
                  {STATUS_LABELS[currentDocument.status]}
                </Badge>
              </div>
            </div>

            {/* Editor */}
            <div className="flex-1 overflow-hidden">
              <MarkdownEditor
                content={content}
                onChange={setContent}
                onSave={handleSave}
                isSaving={isSaving}
                placeholder={`# ${currentDocument.title}

## Overview
문서 내용을 작성하세요...

## Details
- 항목 1
- 항목 2

## Notes
추가 메모...
`}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
